//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import RiderPersonaActions from 'common/redux/actions/riderPersona';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import SkillActions from 'common/redux/actions/skill';
import SkillSelectors from 'common/redux/selectors/skill';
import ToastActions from 'common/redux/actions/toast';

import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';

import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import RiderPersonaSelectors from 'common/redux/selectors/riderPersona';
import {
    getEntitySettings,
    getClientDimensionsMapping
} from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';

import { withRouter } from 'common/components/core/router';

import SingleRiderPersonaEditContainer from './SingleRiderPersonaEditContainer';
import RiderPersonaMasterImportContainer from './RiderPersonaMasterImportContainer';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import theme from 'common/entity/BulkEditContainer.scss';

import { dashboardColors } from 'common/enums/ColorEnum';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import {
    ConnectedSkillSelectorFilter,
    ConnectedSkillIdSelectorFilter
} from 'common/entity/components/SkillSelectorFilter';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import { volumeUnitOptions } from 'common/enums/VolumeUnitEnum';

import type { RiderPersona as MasterRiderPersona } from 'common/models/internal/MasterRiderPersona';
import {
    RiderPersonaTypeEnum,
    FleetTypeEnum
} from 'common/models/internal/MasterRiderPersona';
import {
    RESOURCE_PATH,
    CustomColumnHelpers
} from 'common/entity/helpers/CustomColumnHelpers';

import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { Skill } from 'common/models/internal/Skill';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import VisitUtils from 'common/utils/VisitUtils';
import {
    resourceLimitCellFactory,
    getTransactionTimePerTask
} from 'common/entity/components/resourceLimit/ResourceLimitCell';

import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import SkillsList from 'common/components/core/common/SkillsList';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/riderPersonaMaster';
import { RoleOperationEnum } from 'common/modules/rbac';
import MetricColumnHelper from 'common/entity/helpers/MetricColumnHelper';

const getLimits = riderPersona => riderPersona.limits;

const selectSkillsId = ({ id }) => id;

const booleanFilterOptions = ({ column }) => {
    const { t } = column.getHeaderProps();
    const optionsForSelect = [
        { value: true, label: t('riderPersonaTable.boolean.yes') },
        { value: false, label: t('riderPersonaTable.boolean.no') }
    ];
    return optionsForSelect;
};

const getSkillIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const skills = values.map(skill => ({
        clientId,
        id: skill
    }));
    return new FilterBuilder()
        .withName('skills')
        .withOperation(FilterOperation.Contains)
        .withValues(skills)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

const DEFAULT_COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        frozen: true,
        disablePinControl: true,
        sortable: false,
        filterable: false,
        width: 40
    },
    {
        headerTextId: 'riderPersonaTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'riderPersonaTable.code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'riderPersonaTable.skillId',
        id: 'skills.id',
        sortable: false,
        FilterComponent: ConnectedSkillIdSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = original.skills;
            return skillIds ? (
                <SkillsList
                    skills={skills}
                    skillIds={skillIds}
                    labelSelector={selectSkillsId}
                />
            ) : null;
        }
    },
    {
        headerTextId: 'riderPersonaTable.skillName',
        id: 'skills.name',
        sortable: false,
        FilterComponent: ConnectedSkillSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = original.skills;
            return skillIds ? (
                <SkillsList skills={skills} skillIds={skillIds} />
            ) : null;
        }
    },
    {
        headerTextId: 'riderPersonaTable.name',
        accessor: 'name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.type',
        accessor: 'type',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('type')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = RiderPersonaTypeEnum.getOptions({
                translationKey: 'masterRiderPersonaProperties.riderPersonaType'
            });
            return options;
        }
    },
    {
        headerTextId: 'riderPersonaTable.fleetType',
        accessor: 'fleetType',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('fleetType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = FleetTypeEnum.getOptions({
                translationKey: 'masterRiderPersonaProperties.fleetTypes'
            });
            return options;
        },
        Cell: props => {
            const {
                original: { fleetType },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return fleetType ? (
                <span>
                    {t(`masterRiderPersonaProperties.fleetTypes.${fleetType}`)}
                </span>
            ) : null;
        }
    },
    {
        headerTextId: 'riderPersonaTable.transporterId',
        id: 'transporterId',
        fieldId: 'transporterId',
        accessor: 'transporterId.transporterId',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'riderPersonaTable.transporter',
        id: 'transporter',
        fieldId: 'transporter',
        accessor: 'transporter.name',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'riderPersonaTable.volumeUnit',
        accessor: 'volumeUnit',
        exportId: 'limits.volume.unit',
        property: 'limits.volume.unit',
        FilterComponent: FuelTypeFilter,
        Cell: ({
            original: riderPersona,
            tdProps: {
                rest: { t }
            }
        }) => {
            if (riderPersona.limits?.volume?.unit) {
                return t(
                    `translations:volumeUnits.${riderPersona.limits.volume.unit}`
                );
            }
            return null;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('limits.volume.unit')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => volumeUnitOptions
    },
    {
        headerTextId: 'riderPersonaTable.limits.transactionTimePerTask',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes')
        }),
        id: 'transactionTimePerTask',
        fieldId: 'limits.transactionTimePerTask',
        property: 'limits.transactionTimePerTask',
        filterable: false,
        accessor: (data: MasterRiderPersona) =>
            getTransactionTimePerTask(data.limits?.transactionTimePerTask),
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: MetricColumnHelper.getDistanceColumnProps(
            'riderPersonaProperties.limits.distance'
        ),
        id: 'minDistance',
        fieldId: 'limits.distance.lowerThresh',
        property: 'limits.distance.lowerThresh',
        minWidth: 100,
        infixSearchable: true,
        Cell: resourceLimitCellFactory('distance', 'lowerThresh', getLimits)
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: MetricColumnHelper.getDistanceColumnProps(
            'riderPersonaProperties.limits.distance'
        ),
        id: 'maxDistance',
        fieldId: 'limits.distance.higherThresh',
        property: 'limits.distance.higherThresh',
        Cell: resourceLimitCellFactory('distance', 'higherThresh', getLimits),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: MetricColumnHelper.getWeightColumnProps(
            'riderPersonaProperties.limits.weight'
        ),
        id: 'minWeight',
        fieldId: 'limits.weight.lowerThresh',
        property: 'limits.weight.lowerThresh',
        Cell: resourceLimitCellFactory('weight', 'lowerThresh', getLimits),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: MetricColumnHelper.getWeightColumnProps(
            'riderPersonaProperties.limits.weight'
        ),
        id: 'maxWeight',
        fieldId: 'limits.weight.higherThresh',
        property: 'limits.weight.higherThresh',
        Cell: resourceLimitCellFactory('weight', 'higherThresh', getLimits),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.minutes'),
                limit: t('riderPersonaProperties.limits.travelTime')
            };
        },
        id: 'minTravelTime',
        fieldId: 'limits.travelTime.lowerThresh',
        property: 'limits.travelTime.lowerThresh',
        Cell: resourceLimitCellFactory('travelTime', 'lowerThresh', getLimits),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.minutes'),
                limit: t('riderPersonaProperties.limits.travelTime')
            };
        },
        id: 'maxTravelTime',
        fieldId: 'limits.travelTime.higherThresh',
        property: 'limits.travelTime.higherThresh',
        Cell: resourceLimitCellFactory('travelTime', 'higherThresh', getLimits),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.task')
            };
        },
        id: 'minTask',
        accessor: 'limits.task.lowerThresh',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.task')
            };
        },
        id: 'maxTask',
        accessor: 'limits.task.higherThresh',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.location')
            };
        },
        id: 'minLocation',
        accessor: 'limits.location.lowerThresh',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.location')
            };
        },
        id: 'maxLocation',
        accessor: 'limits.location.higherThresh',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterRiderPersonaProperties.resourceLimits.totalTime')
        }),
        id: 'minTotalTime',
        fieldId: 'limits.totalTime.lowerThresh',
        accessor: 'limits.totalTime.lowerThresh',
        Cell: resourceLimitCellFactory('totalTime', 'lowerThresh', getLimits),
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterRiderPersonaProperties.resourceLimits.totalTime')
        }),
        id: 'maxTotalTime',
        fieldId: 'limits.totalTime.higherThresh',
        accessor: 'limits.totalTime.higherThresh',
        Cell: resourceLimitCellFactory('totalTime', 'higherThresh', getLimits),
        minWidth: 100
    },
    {
        headerTextId: 'riderPersonaTable.limits.minAllowedLimitWithoutUnits',
        translationProps: ({ t }) => {
            return {
                limit: t('riderPersonaProperties.limits.volume')
            };
        },
        id: 'minVolume',
        fieldId: 'limits.volume.lowerThresh',
        property: 'limits.volume.lowerThresh',
        Cell: resourceLimitCellFactory(
            'volume',
            'lowerThresh',
            getLimits,
            true
        ),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.limits.maxAllowedLimitWithoutUnits',
        translationProps: ({ t }) => {
            return {
                limit: t('riderPersonaProperties.limits.volume')
            };
        },
        id: 'maxVolume',
        fieldId: 'limits.volume.higherThresh',
        property: 'limits.volume.higherThresh',
        Cell: resourceLimitCellFactory(
            'volume',
            'higherThresh',
            getLimits,
            true
        ),
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'riderPersonaTable.allowMultiTrip',
        accessor: 'allowMultiTrip',
        sortable: false,
        minWidth: 100,
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { allowMultiTrip } = original;
            return allowMultiTrip
                ? t('riderPersonaTable.boolean.yes')
                : t('riderPersonaTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: booleanFilterOptions
    },
    {
        headerTextId: 'riderPersonaTable.enableAuth',
        accessor: 'enableAuth',
        sortable: false,
        minWidth: 100,
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        Cell: props => {
            const {
                original: { enableAuth },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return enableAuth
                ? t('riderPersonaTable.boolean.yes')
                : t('riderPersonaTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: booleanFilterOptions
    },
    {
        headerTextId: 'riderPersonaTable.enableLotr',
        accessor: 'enableLotr',
        sortable: false,
        minWidth: 100,
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        Cell: props => {
            const {
                original: { enableLotr },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return enableLotr
                ? t('riderPersonaTable.boolean.yes')
                : t('riderPersonaTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: booleanFilterOptions
    },
    {
        headerTextId: 'riderPersonaTable.enablePjp',
        accessor: 'enablePjp',
        sortable: false,
        minWidth: 100,
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        Cell: props => {
            const {
                original: { enablePjp },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return enablePjp
                ? t('riderPersonaTable.boolean.yes')
                : t('riderPersonaTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: booleanFilterOptions
    },
    {
        headerTextId: 'riderPersonaTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: riderPersona,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                riderPersona?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'riderPersonaTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'riderPersonaTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: riderPersona,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                riderPersona?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'riderPersonaTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'riderPersonaTable.tatEfficiency',
        accessor: 'competency.tatEfficiency',
        id: 'tatEfficiency',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'riderPersonaTable.travelTimeEfficiency',
        accessor: 'competency.travelTimeEfficiency',
        id: 'travelTimeEfficiency',
        sortable: false,
        filterable: false
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type RiderPersonasDataType = {
    riderPersonas: Array<MasterRiderPersona>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: RiderPersonasDataType,
    entitySettings: ?EntitySettings,
    isGlobalPersonnel: any,
    tableSettings: ?Array<Object>,
    t: TFunction,
    skills: Array<Skill>,
    fetchSkills: ({ clientId: string }) => void,
    fetchOptions: () => {}
};

const riderPersonaMasterContainerFactory = ({
    EntityEditViewContainer = SingleRiderPersonaEditContainer,
    EntityImportContainer = RiderPersonaMasterImportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            add: RoleOperationEnum.UPDATE_RIDER_PERSONA,
            edit: RoleOperationEnum.UPDATE_RIDER_PERSONA,
            export: RoleOperationEnum.EXPORT_RIDER_PERSONA
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = RiderPersonaActions.fetchActiveEntities
}) => {
    const RiderPersonaMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer: MasterExportContainer,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class RiderPersonaMasterContainer extends PureComponent<Props> {
        componentDidMount = () => {
            this.fetchOtherResources();
        };

        componentDidUpdate = (prevProps: Props) => {
            const { clientId } = this.props;
            if (clientId !== prevProps.clientId) {
                this.fetchOtherResources();
            }
        };

        fetchOtherResources = () => {
            const { clientId, fetchSkills } = this.props;
            fetchSkills({ clientId });
        };

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                theme,
                skills,
                isGlobalPersonnel,
                unitConverter
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                theme,
                isChecked,
                skills,
                isGlobalPersonnel,
                unitConverter
            };
        };

        // PJP fields are to be hidden for non-PJP clients
        areFieldsHidden = () => {
            return !this.props.entitySettings.pjpSettings.enabled;
        };

        render() {
            const { clientId, fetchOptions, t, entitySettings } = this.props;
            const riderPersonaFetchOptions = {
                clientId,
                include: 'TRANSPORTER',
                ...(fetchOptions || {})
            };
            const customResourceLimitsColumns = entitySettings
                ? CustomColumnHelpers.getCustomResourceColumns(
                      entitySettings,
                      'riderPersonaSettings',
                      RESOURCE_PATH
                  )
                : [];

            return (
                <RiderPersonaMaster
                    {...this.props}
                    emptyStateImageSrc="/img/riderPersona-empty.png"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'riderPersonaId'}
                    entitySettingsName={'riderPersonaSettings'}
                    fetchOptions={riderPersonaFetchOptions}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    isFieldHidden={this.areFieldsHidden}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    enableCascadeWarning
                    customResourceLimitsColumns={customResourceLimitsColumns}
                    useLocalisation={false}
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = RiderPersonaSelectors.isEntitiesLoading(
            state,
            ownProps
        );
        const data = RiderPersonaSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const skills = SkillSelectors.getEntities(state, ownProps);
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

        const dimensionsMapping = getClientDimensionsMapping(state, ownProps);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }

        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            isImportEntityActive,
            skills,
            isExportEntityActive,
            isGlobalPersonnel,
            unitConverter
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        fetchSkills: SkillActions.fetchEntities,
        disableEntity: RiderPersonaActions.disableEntity,
        clearData: RiderPersonaActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkDisableEntity: RiderPersonaActions.bulkDisableEntity
    });

    const enhance = compose(
        themr(null, theme),
        withRouter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(RiderPersonaMasterContainer);
};

const ThemedConnectedRiderPersonaMasterContainer = riderPersonaMasterContainerFactory(
    {}
);

export {
    riderPersonaMasterContainerFactory,
    ThemedConnectedRiderPersonaMasterContainer as RiderPersonaMasterContainer
};
export default ThemedConnectedRiderPersonaMasterContainer;
